import { Howl } from 'howler';

const SFX_KEYS = [
  'click', 'woof', 'hey', 'whimper', 'wow', 'yeah', 'yippee',
  'goodjob', 'megapup', 'levelsuccess', 'endoflevel', 'ohnooo',
  'thunder', 'princessbark', 'dogbowl',
  // Restored from an older Puppies (Swift) build — referenced by iOS
  // playRandomGoodJob but absent from the puppies2026 source mirror.
  'bravo', 'brilliant', 'cool', 'great', 'outstanding', 'unbelievable',
] as const;
export type SfxKey = (typeof SFX_KEYS)[number];

const MUSIC_KEYS = ['bkgndmusic1', 'bkgndmusic2', 'bkgndmusic3', 'bkgndmusic4'] as const;
export type MusicKey = (typeof MUSIC_KEYS)[number];

export class SoundBank {
  private sfx: Map<SfxKey, Howl> = new Map();
  private music: Map<MusicKey, Howl> = new Map();
  private musicEnabled = true;
  private sfxEnabled = true;
  private currentMusic: Howl | null = null;
  // Mirrors iOS PuppyGame.lastSound — playRandomGoodJob avoids repeating
  // the index it just picked.
  private lastGoodJobIndex = -1;

  constructor(basePath = './assets/audio') {
    for (const k of SFX_KEYS) {
      this.sfx.set(k, new Howl({ src: [`${basePath}/${k}.mp3`], volume: 0.7 }));
    }
    for (const k of MUSIC_KEYS) {
      this.music.set(
        k,
        // preload:false — defer the ~2-3 MB track download until first play
        // (startMusic in MainScene). Keeps music off the startup critical path.
        new Howl({ src: [`${basePath}/${k}.mp3`], volume: 0.4, loop: true, preload: false }),
      );
    }
  }

  playSfx(key: SfxKey): number | undefined {
    if (!this.sfxEnabled) return undefined;
    return this.sfx.get(key)?.play();
  }

  // Stop a single playing instance (when `id` is given) or every instance
  // of that sound (when omitted). Used for long sfx whose visual cue ends
  // before the audio file would naturally finish.
  stopSfx(key: SfxKey, id?: number): void {
    const h = this.sfx.get(key);
    if (!h) return;
    if (id !== undefined) h.stop(id);
    else h.stop();
  }

  startMusic(key: MusicKey): void {
    this.stopMusic();
    if (!this.musicEnabled) return;
    const h = this.music.get(key);
    if (!h) return;
    // Music Howls are preload:false (kept off the boot critical path). Howler's
    // play() queues the play but never triggers the load on its own, so kick it
    // off explicitly when unloaded — the queued play flushes once it loads.
    if (h.state() === 'unloaded') h.load();
    h.play();
    this.currentMusic = h;
  }

  stopMusic(): void {
    this.currentMusic?.stop();
    this.currentMusic = null;
  }

  setMusicEnabled(on: boolean): void {
    this.musicEnabled = on;
    if (!on) this.stopMusic();
  }

  setSfxEnabled(on: boolean): void {
    this.sfxEnabled = on;
  }

  // Mirrors iOS PuppyGame.playRandomGoodJob (PuppyGame.swift:509) — picks one
  // of 10 sounds and avoids repeating the previous pick. iOS itself never
  // shipped the bravo/brilliant/cool/great/outstanding/unbelievable .caf
  // files in puppies2026, so those slots were silent on device; we restored
  // them from an older Puppies (Swift) build so the full pool is audible.
  playRandomGoodJob(): void {
    const pool: SfxKey[] = [
      'bravo', 'brilliant', 'cool', 'great', 'outstanding', 'unbelievable',
      'yeah', 'yippee', 'wow', 'goodjob',
    ];
    let i = this.lastGoodJobIndex;
    while (i === this.lastGoodJobIndex) i = Math.floor(Math.random() * pool.length);
    this.lastGoodJobIndex = i;
    this.playSfx(pool[i]!);
  }
}
